/* global PLATFORM, G */
import { Manager } from 'socket.io-client'
import { API_URL } from 'lib/util/environment'
import format from 'lib/util/string'
import api from './api'

// adds /:type/:parentId/count/:relation route and aggregate function

const getNamespace = ns => `/${ns}`
const ns = {
  ref: 'reference', // id_type
  doc: 'document', // id
  alert: 'alert', // id
}

/**
 * @typedef {Object} GlobalRoomType
 * @description Type used to define global room configurations
 * @property {string} namespace the websocket namespace used by the room
 * @property {string} keyFormat the key used to connect to the room
 * @property {boolean} reset    whether G.SET event should be dispatched on received message
 */

/**
 * Global types the client should derive
 * and subscribe a room from.
 * @type {Object.<string, GlobalRoomType>}
 */
export const globalRoomTypes = {
  message: {
    namespace: 'ref',
    keyFormat: '{key}_{type}',
  },
  alert: {
    namespace: 'alert',
    keyFormat: '{key}',
    reset: PLATFORM === 'web',
  },
}

/**
 * Initialized global room types
 * @type {Object.<string, GlobalRoomType>}
 */
let activeGlobalRoomTypes = {}

const transports = [
  'websocket',
]

let _doc
let _ref
let _alert

/**
 * PubSub Manager
 *
 * Uses Steam.IO Client instance.
 *
 * TODO: Refactor PubSub Manager so that room's are tied to
 *       specific user roles / permissions (SP-957)
 *
 * @param obj
 * @returns {{ref: api, doc: api}}
 */
const pubSubManager = (obj) => {
  const manager = new Manager(API_URL, { transports, autoConnect: false })

  _doc = manager.socket(getNamespace(ns.doc))
  _ref = manager.socket(getNamespace(ns.ref))
  _alert = manager.socket(getNamespace(ns.alert))

  const channels = {
    /**
     * @type {api} socket.io API
     */
    doc: api(obj)(_doc),

    /**
     * @type {api} socket.io API
     */
    ref: api(obj)(_ref),

    /**
     * @type {api} socket.io API
     */
    alert: api(obj)(_alert),
  }

  const eventBus = obj[G.EVENTS]

  const roomEventListener = (type, typeName) => (event) => {
    eventBus.dispatch(eventBus.type(G.NOTIFICATION, G.CREATE, typeName), { [G.DATA]: event })
    type.reset && eventBus.dispatch(eventBus.type(G.NOTIFICATION, G.SET, typeName), {})
  }

  eventBus.add(eventBus.type(G.DATA, G.INIT), (event) => {
    channels.doc.connect()
    channels.ref.connect()
    channels.alert.connect()

    // detail's G.PUBSUB can return a subset of `globalRoomTypes`
    activeGlobalRoomTypes = event.detail[G.PUBSUB]
      ? event.detail[G.PUBSUB](globalRoomTypes)
      : globalRoomTypes

    Object.keys(activeGlobalRoomTypes).forEach((typeName) => {
      const type = activeGlobalRoomTypes[typeName]
      const { key } = event.detail[G.DATA]

      const room = format(type.keyFormat, { key, type: typeName })

      channels[type.namespace].sub(room, () => {
        channels[type.namespace].on(room, roomEventListener(type, typeName))
      })
    })
  })

  eventBus.add(eventBus.type(G.DATA, G.DESTROY), (event) => {
    channels.doc.disconnect()
    channels.ref.disconnect()
    channels.alert.disconnect()

    Object.keys(activeGlobalRoomTypes).forEach((typeName) => {
      const type = activeGlobalRoomTypes[typeName]
      const { key } = event.detail[G.DATA]

      const room = format(type.keyFormat, { key, type: typeName })

      channels[type.namespace].unsub(room, () => {
        channels[type.namespace].off(room, roomEventListener(typeName))
      })
    })
  })

  return channels
}

export default pubSubManager
